/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React contexts
import { useMaterialUIController } from "context";
import pxToRem from "assets/theme/functions/pxToRem";
import { Colors } from "utils/Constants";

function DataTableHeadCell({
  width,
  children,
  sorted,
  align,
  backgroundColor,
  textColor,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <MDBox
      component="th"
      width={width}
      py={1}
      px={1.5}
      sx={({ borders: { borderWidth } }) => ({
        border: `${borderWidth[1]} solid rgba(246, 246, 248, 0.1)`,
        borderRight: `${borderWidth[1]} solid #fff`,
        backgroundColor: backgroundColor || Colors.PRIMARY,
      })}
    >
      <MDBox
        {...rest}
        position="relative"
        textAlign={align}
        color={textColor || (darkMode ? Colors.WHITE : Colors.WHITE)}
        sx={() => ({
          fontSize: pxToRem(14),
          // fontWeight: fontWeightBold,
          fontWeight: 500,
          cursor: sorted && "pointer",
          userSelect: sorted && "none",
          minWidth: "max-content",
        })}
      >
        {children}
        {sorted && (
          <MDBox
            position="absolute"
            top={0}
            right={align !== "right" ? "16px" : 0}
            left={align === "right" ? "-5px" : "unset"}
            sx={({ typography: { size } }) => ({
              fontSize: size.lg,
            })}
          >
            <MDBox
              position="absolute"
              top={-6}
              color={sorted === "asce" ? "text" : "secondary"}
              opacity={sorted === "asce" ? 1 : 0.5}
            >
              <Icon>arrow_drop_up</Icon>
            </MDBox>
            <MDBox
              position="absolute"
              top={0}
              color={sorted === "desc" ? "text" : "secondary"}
              opacity={sorted === "desc" ? 1 : 0.5}
            >
              <Icon>arrow_drop_down</Icon>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of DataTableHeadCell
DataTableHeadCell.defaultProps = {
  width: "auto",
  sorted: "none",
  align: "left",
  backgroundColor: Colors.PRIMARY,
  textColor: Colors.WHITE,
};

// Typechecking props for the DataTableHeadCell
DataTableHeadCell.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  sorted: PropTypes.oneOf([false, "none", "asce", "desc"]),
  align: PropTypes.oneOf(["left", "right", "center"]),
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
};

export default DataTableHeadCell;
